// src/lib/meetings/meetingOutbox.ts
//
// Isolated offline outbox for meeting edge-fn submissions. Deliberately SEPARATE from
// the audio capture queue (src/lib/native/offlineQueue.ts) so the shared capture
// drain can never mis-handle a meeting item (and vice-versa). localStorage-backed;
// the id is the client_idempotency_key, so re-posting on reconnect is safe (the
// server-side UNIQUE idempotency-key constraint dedupes each target fn).
//
// Multiple meeting flows share this one outbox. Each item carries the target edge-fn
// `slug` so a single drain routes correctly: `meeting-book` (Request/Booked) or
// `meeting-report-submit` (Report Writer). The slug defaults to `meeting-book` so
// pre-existing queued items (written before the slug field existed) still drain.
//
// RETRY IS NOW BOUNDED (2026-08-18). `attempts`, `lastError` and `createdAt` were written on every
// failed drain and read by NOTHING, so an item the server had already refused was re-posted on
// every mount of the three meeting pages and on every window `online` event, for as long as the
// browser profile survived. The refusals reachable here do not change with repetition:
// meeting-report-submit answers 401 on an expired session (index.ts:88), 404 when the booking is
// not visible under RLS (:135), and 403/400 from the insert guard (:207) — re-sending the identical
// body under the identical idempotency key can only be refused identically. Three bounds now apply,
// in the shape the sibling capture queue already uses (src/lib/capture/drainCaptures.ts:42, :205):
//   * permanent-error classification — a 4xx other than 408/429 leaves the loop on the FIRST refusal
//   * MAX_DRAIN_ATTEMPTS failed drains
//   * MAX_QUEUED_AGE_MS since the item was staged
// An item that hits a bound is PARKED, never deleted: it keeps its payload and gains `failedAt` /
// `failedReason` / `lastStatus`, and the drain skips it from then on. Parking rather than dropping
// matters because the payload is work somebody typed — in the Report Writer case, a whole
// fund-manager appraisal.
//
// ⚠️ A PARKED ITEM IS STILL NOT SURFACED IN THE UI. `listMeetings` (below) has no caller anywhere
// in src/, so a parked submission is reachable only from devtools plus the single console.warn each
// park emits. The missing half of this fix is a "queued submissions" view that calls listMeetings()
// and offers a retry; until that exists, the bound stops the infinite loop but does not tell the
// analyst their work was refused.
//
// NOTE: document blobs are NOT persisted here — files are uploaded to storage on the
// first (online) attempt and their storage paths are folded into the payload, so a
// later drain re-posts with the same attachment_keys. A submission attempted fully
// offline simply carries no attachments until it drains.

import { supabase } from "@/integrations/supabase/client";

const KEY = "cbam_meeting_outbox_v1";
const DEFAULT_SLUG = "meeting-book";

// Same NAME and same SEMANTICS as the capture queue's cap (src/lib/capture/drainCaptures.ts:42):
// `attempts` counts FAILED drains, and an item leaves the drainable set once it has spent them.
// Kept identical rather than re-tuned so the two client outboxes stay comparable.
const MAX_DRAIN_ATTEMPTS = 6;

// A queued submission is form state with a date inside it: a request/booking names the meeting day,
// a report appraises a meeting that has already happened. Fourteen days is longer than any offline
// stretch a device realistically has here (the drain fires on mount, on reconnect and on every
// `online` event, so a phone that is used at all drains within a day of regaining signal) and short
// enough that the payload has not outlived the meeting it describes.
const MAX_QUEUED_AGE_MS = 14 * 24 * 60 * 60 * 1000;

/** Why an item left the retry loop. Terminal: the drain never posts it again, and never drops it. */
export type MeetingOutboxTerminalReason = "permanent_error" | "attempt_cap" | "age_cap";

export interface MeetingOutboxItem {
  id: string; // client_idempotency_key
  payload: Record<string, unknown>; // { booking, calendar_mode, attachment_keys } | report payload
  slug?: string; // target edge fn; defaults to meeting-book for legacy items
  createdAt: number;
  attempts: number;
  lastError?: string;
  /** HTTP status of the last refusal, when the transport carried one. */
  lastStatus?: number;
  /** Set once the item is parked. Its presence is what makes the item terminal. */
  failedAt?: number;
  failedReason?: MeetingOutboxTerminalReason;
}

function read(): MeetingOutboxItem[] {
  try {
    const raw = localStorage.getItem(KEY);
    const parsed = raw ? JSON.parse(raw) : [];
    return Array.isArray(parsed) ? parsed : [];
  } catch {
    return [];
  }
}

function write(items: MeetingOutboxItem[]): void {
  try {
    localStorage.setItem(KEY, JSON.stringify(items));
  } catch {
    /* quota / unavailable — best-effort */
  }
}

/** Patch one item. Re-reads first, so a write made between the drain's read and this call (another
 *  tab, a concurrent submit) is not clobbered. `undefined` fields are dropped by JSON.stringify,
 *  which is how a revived item sheds its terminal marks. */
function patch(id: string, fields: Partial<MeetingOutboxItem>): void {
  write(read().map((i) => (i.id === id ? { ...i, ...fields } : i)));
}

export function enqueueMeeting(
  id: string,
  payload: Record<string, unknown>,
  slug: string = DEFAULT_SLUG,
): void {
  const items = read();
  if (items.some((i) => i.id === id)) {
    // The same key being staged again means the caller is submitting it right now (the pages mint a
    // fresh uuid per submit, so this is a deliberate re-drive, not a loop). Revive it: a parked item
    // that kept `failedAt` would be skipped by every later drain even though a fresh attempt is
    // under way, and a spent attempt budget would park it again on the very next pass.
    write(
      items.map((i) =>
        i.id === id
          ? {
              ...i,
              payload,
              slug,
              attempts: 0,
              lastError: undefined,
              lastStatus: undefined,
              failedAt: undefined,
              failedReason: undefined,
            }
          : i,
      ),
    );
    return;
  }
  items.push({ id, payload, slug, createdAt: Date.now(), attempts: 0 });
  write(items);
}

export function updateMeetingPayload(id: string, payload: Record<string, unknown>): void {
  write(read().map((i) => (i.id === id ? { ...i, payload } : i)));
}

export function removeMeeting(id: string): void {
  write(read().filter((i) => i.id !== id));
}

/** Every item still in the outbox, live and parked. ⚠️ Nothing calls this yet — see the header. */
export function listMeetings(): MeetingOutboxItem[] {
  return read();
}

/**
 * A meeting submission the edge fn refused, carrying the HTTP status the drain classifies on.
 * postMeeting used to `throw new Error(error.message)`, which discarded the FunctionsHttpError and
 * with it `context` (the Response) — so no caller, here or in the pages, could tell a 403 from a
 * dropped connection. `status` is null for a relay/fetch failure, which is what offline looks like.
 */
export class MeetingPostError extends Error {
  readonly status: number | null;
  /** The original supabase-js error, kept so a caller can still read the response body. */
  readonly cause: unknown;

  constructor(message: string, status: number | null, cause: unknown) {
    super(message);
    this.name = "MeetingPostError";
    this.status = status;
    this.cause = cause;
  }
}

/** The HTTP status behind an invoke() failure, or null when there is none. supabase-js wraps a
 *  non-2xx as a FunctionsHttpError whose `context` IS the Response; a relay/fetch failure carries no
 *  context at all. Duck-typed rather than imported, matching
 *  src/hooks/investment-reporting/useGoldenIngest.ts:140-146. */
function httpStatusOf(error: unknown): number | null {
  const ctx = (error as { context?: { status?: unknown } } | null)?.context;
  if (typeof ctx?.status === "number") return ctx.status;
  const direct = (error as { status?: unknown } | null)?.status;
  return typeof direct === "number" ? direct : null;
}

/** Is a failed post worth sending again?
 *  No status = transport failure = the offline case = retry. 5xx = the server fell over = retry.
 *  408 (timeout) and 429 (rate limit) are the two 4xx that mean "try again". Every other 4xx is a
 *  refusal of THIS body by THIS endpoint — 401 expired session, 403 missing research capability,
 *  404 booking not visible, 400 rejected insert — and the outbox re-posts a byte-identical body
 *  under the same idempotency key, so a further attempt can only be refused identically. */
function isRetryableStatus(status: number | null): boolean {
  if (status === null) return true;
  if (status === 408 || status === 429) return true;
  return status < 400 || status >= 500;
}

/** Post one submission to its target meeting edge fn. Throws MeetingPostError on transport/edge
 *  error — the status is preserved, never flattened into a bare message. */
export async function postMeeting(
  id: string,
  payload: Record<string, unknown>,
  slug: string = DEFAULT_SLUG,
) {
  const { data, error } = await supabase.functions.invoke(slug, {
    body: { client_idempotency_key: id, ...payload },
  });
  if (error) throw new MeetingPostError(error.message, httpStatusOf(error), error);
  return data as {
    ok: boolean;
    booking_id?: string;
    asana_task_gid?: string | null;
    asana_task_url?: string | null;
    calendar_status?: string;
    partial?: Record<string, string>;
    // meeting-report-submit response fields
    report_id?: string;
    status?: string;
    idempotent_replay?: boolean;
  };
}

/** Move an item to its terminal state. It KEEPS its payload and its place in the outbox — never
 *  deleted, because the payload is work somebody typed and this is the only copy of it. */
function park(
  item: MeetingOutboxItem,
  reason: MeetingOutboxTerminalReason,
  detail: string,
  status: number | null = null,
): void {
  patch(item.id, {
    failedAt: Date.now(),
    failedReason: reason,
    lastError: detail,
    lastStatus: status ?? item.lastStatus,
  });
  // The only surface a parked item has today (listMeetings has no caller — see the header), so at
  // least leave a breadcrumb rather than going silent. Bounded: a parked item is skipped from the
  // next pass onwards, so this fires once per item, not once per drain.
  console.warn(
    `meetingOutbox: ${item.slug ?? DEFAULT_SLUG} submission ${item.id} parked (${reason}) after ` +
      `${item.attempts ?? 0} attempts and will not be retried: ${detail}`,
  );
}

/**
 * Drain queued submissions (re-post; the server unique key makes retries safe).
 * Best-effort: an item that fails transiently stays queued with attempts++/lastError; one that is
 * refused permanently, or that has spent its attempt or age budget, is parked (terminal, still
 * readable via listMeetings) so it stops being re-posted on every mount and every `online` event.
 * Returns the number successfully posted.
 */
export async function drainMeetings(): Promise<number> {
  if (typeof navigator !== "undefined" && navigator.onLine === false) return 0;
  let posted = 0;
  for (const item of read()) {
    // Already parked on an earlier pass: skip without posting and without deleting.
    if (item.failedAt) continue;

    if (Date.now() - item.createdAt > MAX_QUEUED_AGE_MS) {
      const days = Math.floor((Date.now() - item.createdAt) / 86_400_000);
      park(item, "age_cap", `staged ${days} days ago; the form state is stale, not delivered`);
      continue;
    }

    if ((item.attempts ?? 0) >= MAX_DRAIN_ATTEMPTS) {
      park(item, "attempt_cap", item.lastError ?? "no error was recorded");
      continue;
    }

    try {
      await postMeeting(item.id, item.payload, item.slug ?? DEFAULT_SLUG);
      removeMeeting(item.id);
      posted += 1;
    } catch (e) {
      const status = e instanceof MeetingPostError ? e.status : null;
      const message = e instanceof Error ? e.message : String(e);
      const retryable = isRetryableStatus(status);
      if (retryable) {
        // Increment from the FRESHEST stored count, not from this pass's snapshot, so a drain
        // running in another tab cannot have its increment silently overwritten (the pre-existing
        // shape of this write; only the status field is new).
        write(
          read().map((i) =>
            i.id === item.id
              ? {
                  ...i,
                  attempts: (i.attempts ?? 0) + 1,
                  lastError: message,
                  lastStatus: status ?? undefined,
                }
              : i,
          ),
        );
      } else {
        park(item, "permanent_error", message, status);
      }
    }
  }
  return posted;
}
